import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πώς να επιλέξω σχέδιο για πρώτο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ξεκίνα από κάτι που έχει προσωπικό νόημα για σένα. Δεν χρειάζεται να είναι τεράστιο statement, συχνά ένα μικρό αλλά ουσιαστικό σχέδιο λειτουργεί καλύτερα." } },
    { "@type": "Question", name: "Ποια είναι τα πιο συνηθισμένα λάθη σε πρώτο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Να ακολουθείς trends, να επιλέγεις βιαστικά και να αντιγράφεις κάτι χωρίς προσαρμογή. Αυτά συνήθως οδηγούν σε regret." } },
    { "@type": "Question", name: "Τι είναι custom τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Custom σχέδιο σημαίνει σχεδιασμένο αποκλειστικά για σένα, βάσει της ιδέας σου, του placement και της αισθητικής σου. Δεν είναι απαραίτητα περίπλοκο." } },
  ],
};

export default function FirstTattooDesignAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πώς να επιλέξεις το σωστό σχέδιο για πρώτο τατουάζ",
      "description": "Ιδέες και συμβουλές για το πρώτο σου tattoo: πώς να ξεκινήσεις από προσωπικό νόημα, να αποφύγεις κοινά λάθη και να χτίσεις σωστά την επιλογή σου.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-24",
      "dateModified": "2026-04-24",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/first-tattoo-design-athens",
      "inLanguage": "el"
    };
    inject('schema-first-design-faq', faqSchema);
    inject('schema-first-design-article', articleSchema);
    return () => {
      document.getElementById('schema-first-design-faq')?.remove();
      document.getElementById('schema-first-design-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Πώς να Επιλέξεις Σχέδιο για Πρώτο Τατουάζ | 210 Tattoo Athens</title>
        <meta name="description" content="Ιδέες και συμβουλές για το πρώτο σου tattoo: πώς να ξεκινήσεις από προσωπικό νόημα, να αποφύγεις κοινά λάθη και να χτίσεις σωστά την επιλογή σου." />
        <link rel="canonical" href="https://210tattoo.gr/blog/first-tattoo-design-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/first-tattoo-design-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/first-tattoo-design-athens" />
        <meta property="og:title" content="Πώς να Επιλέξεις Σχέδιο για Πρώτο Τατουάζ | 210 Tattoo Athens" />
        <meta property="og:description" content="Ιδέες και συμβουλές για το πρώτο σου tattoo: προσωπικό νόημα, κοινά λάθη και custom design." />
        <meta property="og:url" content="https://210tattoo.gr/blog/first-tattoo-design-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('first-tattoo-design-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Πώς να Επιλέξεις Σχέδιο για Πρώτο Τατουάζ | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Συμβουλές για το πρώτο σου tattoo: νόημα, μέγεθος, custom design και τι να αποφύγεις." />
        <meta name="twitter:image" content={ogImage('first-tattoo-design-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Πρώτο Τατουάζ Αθήνα</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Ιδέες και συμβουλές</span>
              <br />
              <span className="text-white">για το πρώτο σου tattoo</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">24 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Το πρώτο τατουάζ έχει συχνά υπερβολικό άγχος γύρω του. Μήπως κάνω λάθος; Μήπως το μετανιώσω; Η αλήθεια είναι ότι αν το προσεγγίσεις σωστά, δεν χρειάζεται να γίνει περίπλοκο.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Start with personal meaning */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ξεκίνα από κάτι που έχει <span className="text-pink-500">προσωπικό νόημα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Το πρώτο τατουάζ δεν χρειάζεται να είναι τεράστιο statement. Μπορεί να είναι κάτι μικρό αλλά ουσιαστικό, και αυτό συχνά λειτουργεί πολύ καλύτερα.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Ένα τατουάζ που έχει νόημα για σένα δεν «φοριέται» με τον καιρό όπως ένα που επέλεξες γιατί το είδες online και φάνηκε cool εκείνη τη στιγμή. Στα studios της Αθήνας, το consultation είναι συνήθως το σημείο όπου γίνεται η μεγαλύτερη αποσαφήνιση: τι θέλεις, γιατί το θέλεις, και τι θα έχει νόημα για σένα σε πέντε χρόνια.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Κάτι που αντιπροσωπεύει εσένα', desc: 'Μια λέξη, ένα σύμβολο, μια ημερομηνία, κάτι που έχει συγκεκριμένη σημασία στη ζωή σου αντέχει πολύ περισσότερο.' },
                { title: 'Δεν χρειάζεται να το εξηγείς', desc: 'Το νόημα δεν χρειάζεται να είναι κατανοητό σε όλους. Μπορεί να είναι αποκλειστικά δικό σου.' },
                { title: 'Αφήνει περιθώριο για εξέλιξη', desc: 'Ένα μικρό αλλά έχοντας νόημα πρώτο τατουάζ αφήνει χώρο για να χτίσεις αργότερα, αν το επιλέξεις.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Size and placement for first tattoo */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Μέγεθος και σημείο που ταιριάζουν <span className="text-pink-500">σε πρώτο tattoo</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Πολλοί ξεκινούν με medium ή smaller pieces σε placements που δίνουν άνεση. Αυτό έχει λογική: δίνει μια πρώτη εμπειρία που σου επιτρέπει να καταλάβεις τι σου αρέσει, χωρίς να δεσμευτείς αμέσως σε κάτι πολύ μεγάλο ή πολύ ορατό.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Μέτριο μέγεθος', desc: 'Αρκετά μεγάλο για να αποδοθεί καλά, αρκετά μικρό ώστε να μην είναι overwhelm για πρώτη φορά. Ο artist μπορεί να σε βοηθήσει να βρεις τις σωστές αναλογίες.' },
                { title: 'Placement με άνεση', desc: 'Μπράτσο, ώμος, εξωτερικός μηρός, περιοχές που είναι σχετικά άνετες και δίνουν καλή επούλωση για αρχή.' },
                { title: 'Ορατότητα βάσει επιλογής', desc: 'Σκέψου αν θέλεις να φαίνεται καθημερινά ή να μπορεί να κρυφτεί. Και οι δύο επιλογές είναι εξίσου σωστές.' },
                { title: 'Χώρος για μέλλον', desc: 'Αν υπάρχει πιθανότητα να θέλεις sleeve ή μεγαλύτερη σύνθεση αργότερα, καλό είναι το πρώτο piece να τοποθετηθεί με αυτό υπόψη.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How custom helps */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς βοηθά <span className="text-pink-500">ένα custom σχέδιο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Custom σημαίνει σχεδιασμένο αποκλειστικά για εσένα, και αυτό κάνει διαφορά στο να νιώθεις σωστά με την επιλογή σου για χρόνια. Ένα πρώτο τατουάζ που είναι custom σού επιτρέπει επίσης να εκφράσεις κάτι που δεν βρίσκεται σε κανένα flash book ή reference site.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ένα custom πρώτο τατουάζ αποφεύγει τον κίνδυνο να βλέπεις το ίδιο σχέδιο σε άλλους ανθρώπους. Στην Αθήνα, όπου τα τατουάζ είναι πλέον πολύ συνηθισμένα, η αποκλειστικότητα ενός custom σχεδίου έχει ακόμα μεγαλύτερη αξία.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Ξεκινά από την ιδέα σου', desc: 'Εικόνα, λέξη ή αίσθηση. Αρκεί ένα σημείο εκκίνησης. Δεν χρειάζεται να φέρεις έτοιμο σχέδιο στο consultation.' },
                { step: '02', title: 'Ο artist το αναπτύσσει', desc: 'Βάσει του placement, του μεγέθους και της αισθητικής σου, το concept γίνεται σχέδιο που ταιριάζει ακριβώς σε σένα.' },
                { step: '03', title: 'Είναι αποκλειστικά δικό σου', desc: 'Κανένας άλλος δεν θα έχει το ίδιο tattoo. Αυτό έχει αξία πέρα από το αισθητικό αποτέλεσμα.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Common beginner mistakes */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Συνηθισμένα λάθη <span className="text-pink-500">που κάνουν οι αρχάριοι</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Τα περισσότερα regrets που βλέπουμε σε cover up consultations έχουν κάτι κοινό: η αρχική επιλογή έγινε χωρίς αρκετή σκέψη. Και τα καλύτερα πρώτα τατουάζ έχουν επίσης κάτι κοινό: ο άνθρωπος γνώριζε ακριβώς γιατί το επέλεξε.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Να ακολουθούν trends', desc: 'Ό,τι είναι viral τώρα ίσως να φαίνεται dated σε 3 χρόνια. Tattoos που βασίζονται σε trend δεν γερνούν καλά, ούτε αισθητικά ούτε ψυχολογικά.' },
                { title: 'Να επιλέγουν βιαστικά', desc: 'Δεν υπάρχει λόγος βιασύνης. Μια απόφαση που θα κουβαλάς για πάντα αξίζει τον χρόνο σου.' },
                { title: 'Να αντιγράφουν χωρίς προσαρμογή', desc: 'Το να δεις ένα τατουάζ που σου αρέσει και να το ζητήσεις ακριβώς ίδιο δεν δίνει custom αποτέλεσμα, και σημαίνει ότι κάποιος άλλος έχει ακριβώς το ίδιο.' },
                { title: 'Να επιλέγουν artist χωρίς research', desc: 'Η τιμή δεν είναι το μόνο κριτήριο. Portfolio, healed δουλειές και consultation πρέπει να μπουν στην εξίσωση.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 approach */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς σχεδιάζουμε μαζί το τατουάζ σου <span className="text-pink-500">στο 210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Με consultation, συζήτηση, placement planning και design που έχει λόγο ύπαρξης. Ειδικά για first timers, αφιερώνουμε χρόνο στο να καταλάβουμε τι θέλεις, και να σε βοηθήσουμε να το αρθρώσεις αν δεν το έχεις ακόμα ξεκαθαρίσει. Πολλά από τα καλύτερα πρώτα τατουάζ που έχουμε κάνει ξεκίνησαν από μια ασαφή ιδέα που πήρε σχήμα μέσα στη συζήτηση.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Consultation χωρίς πίεση', desc: 'Δεν κλείνουμε ραντεβού χωρίς consultation. Και δεν σε πιέζουμε να αποφασίσεις αμέσως.' },
                { title: 'Custom design για εσένα', desc: 'Βάσει της ιδέας σου, ανεξάρτητα αν είναι "μικρή" ή "απλή". Κάθε project αντιμετωπίζεται σοβαρά.' },
                { title: 'Honest guidance', desc: 'Αν κάτι δεν θα δουλέψει, λόγω μεγέθους, placement ή style, θα σου πούμε και θα εξηγήσουμε γιατί.' },
                { title: 'Aftercare support', desc: 'Μετά τη συνεδρία σου δίνουμε οδηγίες φροντίδας και είμαστε διαθέσιμοι για ερωτήσεις κατά την επούλωση.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Σκέφτεσαι πρώτο τατουάζ και θέλεις να το <span className="text-pink-500">χτίσουμε σωστά από την αρχή;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας μήνυμα στο Instagram του 210 Tattoo Athens. Δεν χρειάζεται να έχεις έτοιμη ιδέα, αρκεί να ξεκινήσεις τη συζήτηση.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/first-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Το Πρώτο σου Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρης οδηγός για πρωτάρηδες, από την επιλογή σχεδίου μέχρι την εκτέλεση.</p>
              </Link>
              <Link to="/blog/tattoo-pain-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πονάει το τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πόσο πονάει πραγματικά και ποια σημεία είναι πιο ευαίσθητα.</p>
              </Link>
              <Link to="/blog/pos-briskeis-idea-tatouaz-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς Βρίσκεις Ιδέα για Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πού να ψάξεις έμπνευση και το τεστ του χρόνου.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/lettering-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Lettering Tattoo Athens</a>
              <a href="/dotwork-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Dotwork Tattoo Athens</a>
              <a href="/sleeve-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Sleeve Tattoo Αθήνα</a>
              <a href="/color-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Color Tattoo Αθήνα</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
